import { Stack } from '@mui/material'
import { Alert, FormDatePicker, FormTextField, toIsoDateString, tokens, User } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import {
    TaskAssigneeSelection,
    TaskSelectionOnChange,
} from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskDetails/TaskAssigneeSelection'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { isEmpty } from 'lodash'
import FormFooterButtons from 'ComponentLibrary/src/molecules/Form/FormFooter/FormFooterButtons'
import { Task } from 'ComponentLibrary/src/common/tasks'
import { FormSwitches } from 'ComponentLibrary/src/atoms/forms'
import TaskVisibilityIcon from 'ComponentLibrary/src/molecules/TaskVisibilityIcon/TaskVisibilityIcon'
import { TaskAssignee } from 'ComponentLibrary/src/common/users'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type FormTaskDetailsProps = {
    task: Pick<Task, 'description' | 'due_date' | 'has_limited_visibility'>
    selectedAssignee: TaskAssignee | null
    assigneeOptions: User[]
    onChangeAssignee: TaskSelectionOnChange
    isMutatingTask?: boolean
    onSubmit: (updatedTask: Pick<Task, 'description' | 'due_date' | 'has_limited_visibility'>, onError?: () => void) => void
    onCancel: () => void
    hasVisibilityField?: boolean
    copy?: Partial<FormTaskDetailsCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

type TaskForm = Pick<Task, 'description' | 'due_date' | 'has_limited_visibility'>

export type FormTaskDetailsCopy = {
    title: string
    assignee: string
    updateErrorMessage: string
    updateErrorTitle: string
    hidden: string
    hiddenSecondary: string
    deadline: string
    description: string
    cancelAriaLabel: string
    submitAriaLabel: string
}

const DEFAULT_FORM_TASK_DETAILS_COPY: FormTaskDetailsCopy = {
    title: 'Aufgabendetails',
    assignee: 'Zuständig',
    updateErrorMessage: 'Es ist ein Fehler beim Senden der Änderungen aufgetreten. Bitte erneut versuchen.',
    updateErrorTitle: 'Fehler',
    hidden: 'Versteckt',
    hiddenSecondary: '(unsichtbar für Kunden)',
    deadline: 'Deadline',
    description: 'Beschreibung',
    cancelAriaLabel: 'Cancel',
    submitAriaLabel: 'Submit',
}

export function FormTaskDetails({
    task,
    assigneeOptions,
    selectedAssignee,
    onChangeAssignee,
    isMutatingTask,
    onCancel,
    onSubmit,
    hasVisibilityField,
    copy,
    userLinkCopy,
    locale,
}: FormTaskDetailsProps) {
    const resolvedCopy = { ...DEFAULT_FORM_TASK_DETAILS_COPY, ...copy }
    const formValues = useMemo(() => {
        return {
            description: task.description,
            due_date: task.due_date,
            has_limited_visibility: task.has_limited_visibility,
        }
    }, [task.description, task.due_date, task.has_limited_visibility])

    const {
        control,
        formState: { errors, dirtyFields },
        handleSubmit,
        reset,
        setError,
    } = useForm<TaskForm>({
        defaultValues: formValues,
    })

    const hasLimitedVisibility = useWatch({ control, name: 'has_limited_visibility' })

    const handleCancel = () => {
        reset()
        onCancel()
    }

    const submitForm = (data: TaskForm) => {
        onSubmit(
            {
                ...data,
                due_date: data.due_date ? toIsoDateString(data.due_date) : undefined,
            },
            () => {
                setError('root', {
                    type: 'server',
                    message: resolvedCopy.updateErrorMessage,
                })
            }
        )
    }

    useEffect(
        function updateDefaultValues() {
            reset(formValues)
        },
        [formValues, reset]
    )

    return (
        <Stack
            padding={`${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`}
            height="100%"
            width="100%"
            boxSizing="border-box"
            gap={tokens.SizingSpacing200}
        >
            <Stack direction="row">
                <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                    {resolvedCopy.title}
                </Typography>
            </Stack>
            <Stack gap={tokens.SizingSpacing200}>
                <Stack gap={tokens.SizingSpacing050}>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {resolvedCopy.assignee}
                    </Typography>
                    <TaskAssigneeSelection
                        assignee={selectedAssignee}
                        options={assigneeOptions}
                        onChangeAssignee={onChangeAssignee}
                        userLinkCopy={userLinkCopy}
                    />
                </Stack>
            </Stack>
            {errors.root && (
                <Alert title={resolvedCopy.updateErrorTitle} severity="error" size="small">
                    {resolvedCopy.updateErrorMessage}
                </Alert>
            )}
            <Stack gap={tokens.SizingSpacing200}>
                {hasVisibilityField && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <FormSwitches
                            control={control}
                            options={[
                                {
                                    label: resolvedCopy.hidden,
                                    secondaryLabel: resolvedCopy.hiddenSecondary,
                                    formInputName: 'has_limited_visibility',
                                },
                            ]}
                            style={{ paddingLeft: 0 }}
                        />
                        <TaskVisibilityIcon isLimitedVisibility={!!hasLimitedVisibility} size="small" />
                    </Stack>
                )}
                <FormDatePicker
                    size="small"
                    control={control}
                    errors={errors}
                    name="due_date"
                    label={resolvedCopy.deadline}
                    locale={locale}
                />
                <FormTextField
                    fullWidth
                    control={control}
                    errors={errors}
                    label={resolvedCopy.description}
                    multiline
                    maxRows={6}
                    fieldName="description"
                    resize="none"
                />
                <Stack direction="row" width="100%" justifyContent="end" gap={tokens.SizingSpacing100}>
                    <FormFooterButtons
                        onSubmit={handleSubmit(submitForm)}
                        onCancel={handleCancel}
                        isPersisting={isMutatingTask}
                        shouldDisableSubmit={isEmpty(dirtyFields)}
                        copy={{
                            cancelAriaLabel: resolvedCopy.cancelAriaLabel,
                            submitAriaLabel: resolvedCopy.submitAriaLabel,
                        }}
                    />
                </Stack>
            </Stack>
        </Stack>
    )
}
